<script setup lang="ts">
/**
 * Application shell — full-viewport flex column that composes the three
 * top-level layout regions.
 *
 * Slots
 *   header  — top bar (`<header>`), rendered in inverted Tufte colors.
 *   sidebar — left instrument bar (`<aside>`); collapses to `width: 0`
 *             when the sidebar is toggled off.
 *   main    — primary content area (`<main>`); expands to fill the space
 *             vacated by the collapsed sidebar.
 *
 * Sidebar collapse
 *   Wired to `useSidebarPanel` (shared Pinia composable). A fixed-position
 *   chevron toggle button tracks the sidebar's right edge via a CSS
 *   transition so it animates in sync with the sidebar width.
 *
 * `collapsible` prop
 *   When `false` (e.g. the logged-out shell, which only holds a language
 *   switcher — nothing worth hiding), the toggle chevron is not rendered at
 *   all and the sidebar ignores any persisted collapsed state from a
 *   previous logged-in session, so there's never a dead end where the
 *   sidebar is collapsed with no control left to reopen it.
 *
 * `h-screen` constraint
 *   The root div uses `h-screen` (not `min-h-screen`) so the viewport is
 *   the scroll boundary for `<main>`. Without this, `position: sticky`
 *   headers inside `<main>` break because the document body becomes the
 *   scroll container.
 */
import { computed } from 'vue'
import { useSidebarPanel } from '@/composables/useSidebarPanel'

const props = withDefaults(defineProps<{ collapsible?: boolean }>(), { collapsible: true })
const { collapsed, toggle } = useSidebarPanel()
const effectiveCollapsed = computed(() => props.collapsible && collapsed.value)
</script>

<template>
  <!--
    `h-screen` (not `min-h-screen`) constrains the shell to exactly the viewport
    height. Without this, tall tab content grows the outer container past the
    viewport, the document/body becomes the actual scroll container, and the
    inner scrollable column never scrolls itself — which in turn breaks
    `position: sticky` headers (the sticky boundary is the scroll ancestor,
    but that ancestor is no longer the scrolling one). With `h-screen`, the
    `flex-1`/`min-h-0` chain on the row and `main` correctly hands `main` a
    bounded height. The actual scroll container is the middle column inside
    `main` (see `App.vue`'s `.middle-column`); `main` itself only clips so the
    `InfoViewLayout` / `ConversationLayout` flanks stay pinned while the
    middle area scrolls.
  -->
  <div class="h-screen flex flex-col bg-tufte-bg text-tufte-text">
    <header class="bg-tufte-text text-tufte-bg">
      <slot name="header"></slot>
    </header>
    <div class="relative flex min-h-0 flex-1">
      <!--
        ASIDE: left instrument bar. The tab panel (see `NavigationButtonPanel`) is
        `position: fixed` for the original look; this aside only reserves its width
        in the flex row and animates to 0 when collapsed so `main` reclaims the space.
      -->
      <aside
        class="hidden md:block shrink-0 overflow-hidden transition-[width] duration-200 ease-out motion-reduce:transition-none"
        :style="{ width: effectiveCollapsed ? '0px' : 'var(--width-instrument-bar)' }"
        aria-hidden="true"
      >
        <slot name="sidebar"></slot>
      </aside>

      <main class="min-h-0 min-w-0 flex-1 overflow-hidden bg-tufte-bg">
        <slot name="main"></slot>
      </main>
    </div>

    <!--
      Floating panel toggle: small muted tab handle fixed to the viewport at mid-height.
      Borders match the rest of the UI (`--color-tufte-border`); top/bottom/right borders
      frame the handle against the sidebar seam. Left edge tracks the sidebar width.
    -->
    <button
      v-if="collapsible"
      type="button"
      class="panel-toggle fixed top-1/2 z-50 hidden md:flex h-8 w-4 -translate-y-1/2 items-center justify-center border-0 border-y border-r border-solid border-tufte-border bg-tufte-bg text-tufte-muted transition-[left] duration-200 ease-out hover:text-tufte-text focus:outline-none focus:ring-1 focus:ring-tufte-text/30 motion-reduce:transition-none"
      :style="{ left: effectiveCollapsed ? '0px' : 'var(--width-instrument-bar)' }"
      :aria-expanded="!effectiveCollapsed"
      aria-controls="instrument-sidebar"
      :aria-label="effectiveCollapsed ? 'Show tab panel' : 'Hide tab panel'"
      :title="effectiveCollapsed ? 'Show tab panel' : 'Hide tab panel'"
      @click="toggle"
    >
      <svg
        class="h-3 w-3 transition-transform duration-200 ease-out motion-reduce:transition-none"
        :class="effectiveCollapsed ? '' : 'rotate-180'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M9 18 L15 12 L9 6" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
/* SCOPED STYLES (Vue):
   The "scoped" attribute makes these styles apply only to this component.
   Vue adds a unique data attribute (e.g. data-v-7ba5bd90) to every element in
   this template and rewrites selectors to include it (e.g. .foo becomes
   .foo[data-v-7ba5bd90]), so the CSS does not affect other components or
   global markup. Child component roots are not styled by this block; use
   :deep() if you need to style slot content or child components.
   Layout here uses Tailwind utilities; colors from tufte_theme.css Tufte theme. */
</style>
